/* eslint-disable no-param-reassign */
/* eslint-disable no-nested-ternary */

/**
 * @name getType
 * @description Returns the specific type of value
 * @param {any} value The value to get the type of
 * @return {String} The type of the value passed in
 */
export const getType = value => Object.prototype.toString.call(value).slice(8, -1).toLowerCase();

/**
 * @name chunk
 * @description Creates an array of elements split into groups the length of size.
 * @param {Array} input The array to split into groups
 * @param {Number} size The size of each chunk
 * @return {Array} An array containing the groups of length size
 */
export const chunk = (input, size) =>
  input.reduce((arr, item, idx) => {
    return idx % size === 0 ? [...arr, [item]] : [...arr.slice(0, -1), [...arr.slice(-1)[0], item]];
  }, []);

/**
 * @name isEqual
 * @description Checks if two items are equal (deep equality)
 * @param {Array|Boolean|Function|Null|Number|Object|String|Undefined} obj1 The first item
 * @param {Array|Boolean|Function|Null|Number|Object|String|Undefined} obj2 The second item
 * @returns {Boolean} True if equal, false otherwise
 */
export const isEqual = (obj1, obj2) => {
  const areArraysEqual = () => {
    if (obj1.length !== obj2.length) return false;

    for (let i = 0; i < obj1.length; i++) if (!isEqual(obj1[i], obj2[i])) return false;

    return true;
  };

  const areFunctionsEqual = () => obj1.toString() === obj2.toString();

  const areObjectsEqual = () => {
    if (Object.keys(obj1).length !== Object.keys(obj2).length) return false;

    return Object.keys(obj1).every(key => isEqual(obj1[key], obj2[key]));
  };

  const type = getType(obj1);

  if (type !== getType(obj2)) return false;

  if (type === 'array') return areArraysEqual();
  if (type === 'function' || type === 'symbol' || [NaN, Infinity].includes(obj1)) return areFunctionsEqual();
  if (type === 'object') return areObjectsEqual();

  return JSON.stringify(obj1) === JSON.stringify(obj2);
};

/**
 * @name searchType
 * @description Returns the first element matching the provided criteria
 * @param {Array} arr The array to search
 * @param {Array|Function|Object|String} criteria The criteria to search by
 * @param {String} type Either 'find' or 'findIndex' to define search type
 * @return {Number} Returns the index of the found element, else -1
 */
export const searchType = (arr, criteria, type) =>
  arr[type](a => {
    switch (getType(criteria)) {
      case 'array': {
        const [field, value] = criteria;
        return a[field] === value;
      }
      case 'function':
        return criteria(a);
      case 'object':
        return Object.keys(criteria).every(k => isEqual(criteria[k], a[k]));
      case 'string':
        return a[criteria];
      default:
        return false;
    }
  });

/**
 * @name groupBy
 * @description Groups items from an array together by some criteria or value.
 * @param {Array} arr The array to group on
 * @param {String|Function} criteria The criteria to group by
 * @return {Object} The grouped object
 */
export const groupBy = (arr, criteria) =>
  arr.reduce((obj, item) => {
    const key = typeof criteria === 'function' ? criteria(item) : item[criteria];

    if (!Object.getOwnPropertyDescriptor(obj, key)) obj[key] = [];

    obj[key].push(item);

    return obj;
  }, {});

/**
 * @name isEmpty
 * @description Checks if obj is an empty object or collection.
 *              Does not support Sets or Maps.
 * @param {Array|Object} obj The object or collection to check emptiness of
 * @returns {Boolean} True if empty, false otherwise
 */
export const isEmpty = obj => [Object, Array].includes((obj || {}).constructor) && !Object.entries(obj || {}).length;

/**
 * @name pick
 * @description Creates an object composed of the picked object properties.
 * @param {Object} object The object to pick keys from
 * @param {Array} keys The keys to pick from object
 * @returns {Object} The newly created object with picked properties
 */
export const pick = (object, keys) =>
  (keys || []).reduce((obj, key) => {
    if (object && !!Object.getOwnPropertyDescriptor(object, key)) obj[key] = object[key];
    return obj;
  }, {});

/**
 * @name sortBy
 * @description Sorts array items by some criteria or value.
 * @param {Array} arr The array to sort
 * @param {String|Function} criteria The criteria to sort by
 * @return {Array} A new array sorted by criteria
 */
export const sortBy = (arr, criteria, asc = true) => {
  const valueToCompare = value => {
    switch (typeof criteria) {
      case 'string':
        return value?.[criteria];
      case 'function':
        return criteria?.(value);
      default:
        return value;
    }
  };

  return arr
    .concat()
    .sort((a, b) =>
      valueToCompare(a) > valueToCompare(b)
        ? asc
          ? 1
          : -1
        : valueToCompare(b) > valueToCompare(a)
        ? asc
          ? -1
          : 1
        : 0
    );
};

/**
 * @name unionBy
 * @description Creates an array of unique values from given arrays
 * @param {Function|String} criteria The criteria to union by
 * @param {Array} arrays The arrays to merge
 * @return {Array} The union of all arrays, unique based on criteria
 */
export const unionBy = (criteria, ...arrays) =>
  arrays.flat().filter(
    (set => item => {
      const key = typeof criteria === 'function' ? criteria(item) : item[criteria];
      return set.has(key) ? false : set.add(key);
    })(new Set())
  );
